import { AIAssistantProps } from 'components/DocRightRail/screens/AIAssistant/Types'
import { ChangeLength } from './Actions/ChangeLength'
import { ChangeTone } from './Actions/ChangeTone'
import { PlagiarismCheck } from './Actions/PlagiarismCheck'
import { SEOAnalysis } from './Actions/SEOAnalysis'
import { TurnInto } from './Actions/TurnInto'
import Styles from './AIAssistant.module.scss'
import { Card } from 'components/DocRightRail/screens/AIAssistant/Card'
import { ImproveQuality } from './Actions/ImproveQuality'
import { InfoSection } from 'components/DocRightRailV2/Info/BlazeInfo'
import { CopyContent } from './Actions/CopyContent'
import { Stackable } from 'components/Stackable/Stackable'
import { SingleAssigneePropertiesContainer } from 'components/SingleAssigneePropertiesContainer/SingleAssigneePropertiesContainer'
import { OptionSelectVariant } from 'common/OptionSelect/OptionSelect'
import { AssigneeType } from 'api/clients/PropertyClient'
import { DocInfoContainer } from 'components/DocInfoContainer/DocInfoContainer'
import { NextActionCardContainer } from 'components/DocRightRail/screens/AIAssistant/NextActionCard'
import { PreviousActionCardContainer } from 'components/DocRightRail/screens/AIAssistant/PreviousActionCard'
import { TextChannelKey, UserRole } from 'types'
import { useCanViewAs } from 'components/Can'
import { DownloadPostMediaOnly } from './Actions/DownloadPostMediaOnly'
import { useInformalFeedbackRequests } from 'hooks/almanacFiles/useInformalFeedbackRequests'
import { ReviewRequestCardContainer } from './ReviewRequestCard/ReviewRequestCardContainer'
import { SEOScoreGauge } from 'components/SEOAnalysis/SEOScoreGauge/SEOScoreGauge'
import { BlogMetadata } from './Actions/BlogMetadata'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { useHideProperties } from 'hooks/useHideProperties'
import { NativeDocInfoContainer } from 'components/DocInfoContainer/NativeDocInfoContainer'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'
import { Text } from 'blaze-ui/Text/Text'
import cx from 'classnames'
import { CONTENT_CHANNELS } from 'components/Wizards/WizardsV3/constants'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'

export const AIAssistant = ({ article, layerId, className }: AIAssistantProps) => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const canViewAs = useCanViewAs(article.company_id)
  const isMember = canViewAs([UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN])
  const hideProperties = useHideProperties()
  const { data: brandPlan } = useCurrentBrandPlan()
  const showTurnInto = isMember && !brandPlan?.has_content_strategies

  const { openRequest, mostRecentClosedRequest } = useInformalFeedbackRequests(article.almanac_file.id || '')
  const request = openRequest || mostRecentClosedRequest
  const isBlog = article.channel === ChannelType.BlogPost

  const showSeoScoreGauge = CONTENT_CHANNELS.includes(article.channel as TextChannelKey)

  if (isSystemRedesignEnabled) {
    return (
      <div className={cx(Styles.rightColumnWrapper, className)}>
        <div className={Styles.rightColumn}>
          {isMember && <ReviewRequestCardContainer request={request} channel={article.channel} assetType="article" />}
          <Card>
            <div className={Styles.sectionTitle}>
              <Text variant="secondary">Prepare to publish</Text>
            </div>
            <div className={Styles.actions}>
              {showSeoScoreGauge && (
                <div className={Styles.seoScoresContainer}>
                  <SEOScoreGauge />
                </div>
              )}

              <SEOAnalysis label="SEO Analysis" tooltip="Check for SEO best practices" />
              <PlagiarismCheck label="Check for Plagiarism" tooltip="Check for plagiarism" />
              {isBlog && <BlogMetadata label="Configure Blog Metadata" tooltip="Configure Blog Metadata" />}

              {article.scheduled_post?.id ? (
                <>
                  <CopyContent
                    label="Copy Text to Clipboard"
                    tooltip="Copy post text to clipboard"
                    scheduledPostId={article.scheduled_post.id}
                  />
                  <DownloadPostMediaOnly
                    label="Download Post Media Only"
                    tooltip="Download post media to your device"
                    article={article}
                  />
                </>
              ) : (
                <CopyContent label="Copy Content" tooltip="Copy all contents in the doc" />
              )}
            </div>
          </Card>
          <Card>
            <div className={Styles.sectionTitle}>
              <Text variant="secondary">Improve your content</Text>
            </div>
            <div className={Styles.actions}>
              <ImproveQuality article={article} layerId={layerId} />
              <ChangeTone article={article} layerId={layerId} />
              <ChangeLength article={article} layerId={layerId} />
              {showTurnInto && <TurnInto article={article} layerId={layerId} />}
            </div>
          </Card>
          <PreviousActionCardContainer article={article} layerId={layerId} />
          <InfoSection title="Doc info">{article && <NativeDocInfoContainer articleKey={article.id} />}</InfoSection>
        </div>
      </div>
    )
  }

  return (
    <Stackable className={className}>
      {isMember && (
        <ReviewRequestCardContainer
          request={request}
          channel={article.channel}
          assetType="article"
          alternativeContent={<NextActionCardContainer article={article} layerId={layerId} />}
        />
      )}
      <PreviousActionCardContainer article={article} layerId={layerId} />
      <Card>
        <span className={Styles.title}>Improve your content</span>
        <div className={Styles.actions}>
          <ImproveQuality article={article} layerId={layerId} />
          <ChangeTone article={article} layerId={layerId} />
          <ChangeLength article={article} layerId={layerId} />
          {showTurnInto && <TurnInto article={article} layerId={layerId} />}
        </div>
      </Card>
      <Card>
        <span className={Styles.title}>Prepare to publish</span>
        <div className={Styles.actions}>
          <SEOScoreGauge />

          <SEOAnalysis label="SEO Analysis" tooltip="Check for SEO best practices" />
          <PlagiarismCheck label="Check for Plagiarism" tooltip="Check for plagiarism" />
          {isBlog && <BlogMetadata label="Configure Blog Metadata" tooltip="Configure Blog Metadata" />}

          {article.scheduled_post?.id ? (
            <>
              <CopyContent
                label="Copy Text to Clipboard"
                tooltip="Copy post text to clipboard"
                scheduledPostId={article.scheduled_post.id}
              />
              <DownloadPostMediaOnly
                label="Download Post Media Only"
                tooltip="Download post media to your device"
                article={article}
              />
            </>
          ) : (
            <CopyContent label="Copy Content" tooltip="Copy all contents in the doc" />
          )}
        </div>
      </Card>
      {!hideProperties && (
        <InfoSection title="Properties" className={Styles.properties}>
          {article && (
            <SingleAssigneePropertiesContainer
              variant={OptionSelectVariant.Filter}
              assignee={{ id: article.id, type: AssigneeType.Article }}
              workspaceId={article.company.key}
              showEducationTooltip
              setShowSection={() => {}}
              className={Styles.propertiesList}
            />
          )}
        </InfoSection>
      )}

      {!hideProperties && (
        <InfoSection title="Doc info">
          {article && (
            <DocInfoContainer
              className={Styles.propertiesList}
              assignee={{ id: article.id, type: AssigneeType.Article }}
            />
          )}
        </InfoSection>
      )}
      {hideProperties && (
        <InfoSection title="Doc info">{article && <NativeDocInfoContainer articleKey={article.id} />}</InfoSection>
      )}
    </Stackable>
  )
}
